import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

export type PendingAuthAttempt = {
  intent: 'signin' | 'signup';
  provider: 'email' | 'google' | 'apple';
  flow: 'password' | 'oauth_web' | 'oauth_native' | 'id_token_native';
  route: string | null;
  startedAt: string;
};

const AUTH_CREATION_CLOCK_SKEW_MS = 60_000;

const PENDING_AUTH_ATTEMPT_KEY = 'auth.pendingAttempt';

function getWebStorage() {
  if (typeof localStorage === 'undefined') return null;
  if (typeof localStorage.getItem !== 'function') return null;
  if (typeof localStorage.setItem !== 'function') return null;
  if (typeof localStorage.removeItem !== 'function') return null;
  return localStorage;
}

function sanitizePendingAuthAttempt(
  input: PendingAuthAttempt | null | undefined,
): PendingAuthAttempt | null {
  if (!input) return null;

  return {
    intent: input.intent,
    provider: input.provider,
    flow: input.flow,
    route: input.route?.slice(0, 200) ?? null,
    startedAt: input.startedAt,
  };
}

export async function setPendingAuthAttempt(
  input: PendingAuthAttempt | null | undefined,
): Promise<void> {
  const sanitized = sanitizePendingAuthAttempt(input);

  if (!sanitized) {
    await clearPendingAuthAttempt();
    return;
  }

  const serialized = JSON.stringify(sanitized);

  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (storage) storage.setItem(PENDING_AUTH_ATTEMPT_KEY, serialized);
    return;
  }

  await SecureStore.setItemAsync(PENDING_AUTH_ATTEMPT_KEY, serialized);
}

export async function getPendingAuthAttempt(): Promise<PendingAuthAttempt | null> {
  let raw: string | null = null;

  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    raw = storage?.getItem(PENDING_AUTH_ATTEMPT_KEY) ?? null;
  } else {
    raw = await SecureStore.getItemAsync(PENDING_AUTH_ATTEMPT_KEY);
  }

  if (!raw) return null;

  try {
    return sanitizePendingAuthAttempt(JSON.parse(raw) as PendingAuthAttempt);
  } catch {
    return null;
  }
}

export async function clearPendingAuthAttempt(): Promise<void> {
  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (storage) storage.removeItem(PENDING_AUTH_ATTEMPT_KEY);
    return;
  }

  await SecureStore.deleteItemAsync(PENDING_AUTH_ATTEMPT_KEY);
}

export async function consumePendingAuthAttempt(): Promise<PendingAuthAttempt | null> {
  const attempt = await getPendingAuthAttempt();
  await clearPendingAuthAttempt();
  return attempt;
}

export function wasAuthUserCreatedDuringAttempt(params: {
  attemptStartedAt: string | null | undefined;
  userCreatedAt: string | null | undefined;
  now?: number;
}): boolean {
  const attemptStartedAtMs = Date.parse(params.attemptStartedAt ?? '');
  const userCreatedAtMs = Date.parse(params.userCreatedAt ?? '');
  if (!Number.isFinite(attemptStartedAtMs) || !Number.isFinite(userCreatedAtMs)) {
    return false;
  }

  const now = params.now ?? Date.now();
  return (
    userCreatedAtMs >= attemptStartedAtMs - AUTH_CREATION_CLOCK_SKEW_MS
    && userCreatedAtMs <= now + AUTH_CREATION_CLOCK_SKEW_MS
  );
}
